import React, { useState, useEffect } from 'react';
import { HiChevronLeft, HiChevronRight, HiDownload, HiEye } from 'react-icons/hi';
import { flyersService } from '../services/flyersService';
import { supabase } from '../supabaseClient';
import './NosFlyers.css';
import GSIDAPDF from '../images/GSIDAPDF.jpg';

function NosFlyers({ edition: customEdition }) {
  const [currentFlyer, setCurrentFlyer] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flyersData, setFlyersData] = useState({
    pageHeader: {
      title: 'Nos Flyers',
      subtitle: 'Découvrez et téléchargez nos documents PDF',
      emptyMessage: 'Aucun flyer disponible pour le moment.'
    },
    flyers: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadFlyersData();
  }, [customEdition]);

  const loadFlyersData = async () => {
    try {
      setLoading(true);
      
      let editionYear = customEdition;
      if (!editionYear) {
        // Get active edition first
        const { data: activeEdition, error: editionError } = await supabase
          .from('editions')
          .select('year')
          .eq('is_active', true)
          .single();
        editionYear = activeEdition?.year || '2025';
      }

      const data = await flyersService.getFlyersData(editionYear);
      
      if (data) {
        setFlyersData(data);
      }
      setError(null);
    } catch (error) {
      setError('Failed to load flyers data');
    } finally {
      setLoading(false);
    }
  };

  const nextFlyer = () => {
    if (isFlipping) return;
    setIsFlipping(true);
    setTimeout(() => {
      setCurrentFlyer((prev) => (prev + 1) % flyersData.flyers.length);
      setIsFlipping(false);
    }, 300);
  };

  const prevFlyer = () => {
    if (isFlipping) return;
    setIsFlipping(true);
    setTimeout(() => {
      setCurrentFlyer((prev) => (prev - 1 + flyersData.flyers.length) % flyersData.flyers.length);
      setIsFlipping(false);
    }, 300);
  };

  const handleDownload = (flyer) => {
    // Create a temporary link to trigger download
    const link = document.createElement('a');
    link.href = flyer.downloadUrl;
    link.download = flyer.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePreview = (flyer) => {
    // Open in new tab for preview
    window.open(flyer.downloadUrl, '_blank');
  };

  const goToFlyer = (index) => {
    if (isFlipping || index === currentFlyer) return;
    setIsFlipping(true);
    setTimeout(() => {
      setCurrentFlyer(index);
      setIsFlipping(false);
    }, 300);
  };

  if (loading) {
    return (
      <div className="nos-flyers-page">
        <div className="nos-flyers-container">
          <div className="flyers-header">
              {/* Replace text with animated loading spinner */}
              <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  // Ensure currentFlyer index is valid
  const validCurrentFlyer = currentFlyer < flyersData.flyers.length ? currentFlyer : 0;

  if (flyersData.flyers.length === 0) {
    return (
      <div className="nos-flyers-page">
        <div className="nos-flyers-container">
          <div className="flyers-header">
            <h1>{flyersData.pageHeader.title}</h1>
            <p>{flyersData.pageHeader.emptyMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nos-flyers-page">
      <div className="nos-flyers-container">
        <div className="flyers-header">
          <h1>{flyersData.pageHeader.title}</h1>
          <p>{flyersData.pageHeader.subtitle}</p>
        </div>

        <div className="flyers-content">
          <div className="flyer-viewer">
            <div className={`flyer-book ${isFlipping ? 'flipping' : ''}`}>
              <div className="book-cover">
                <div className="book-spine"></div>
                <div className="book-front">
                  <img 
                    src={flyersData.flyers[validCurrentFlyer].image}
                    alt={flyersData.flyers[validCurrentFlyer].title}
                    onError={(e) => {
                      e.target.src = GSIDAPDF;
                    }}
                  />
                  <div className="book-shadow"></div>
                </div>
              </div>
              
              <div className="flyer-info">
                <h3>{flyersData.flyers[validCurrentFlyer].title}</h3>
                <p>{flyersData.flyers[validCurrentFlyer].description}</p>
                <div className="flyer-meta">
                  <span className="file-type">PDF</span>
                  {flyersData.flyers[validCurrentFlyer].fileSize && (
                    <span className="file-size">{flyersData.flyers[validCurrentFlyer].fileSize}</span>
                  )}
                </div>
                
                <div className="flyer-actions">
                  <button 
                    className="action-btn preview-btn"
                    onClick={() => handlePreview(flyersData.flyers[validCurrentFlyer])}
                  >
                    <HiEye />
                    <span>Aperçu</span>
                  </button>
                  <button 
                    className="action-btn download-btn"
                    onClick={() => handleDownload(flyersData.flyers[validCurrentFlyer])}
                  >
                    <HiDownload />
                    <span>Télécharger</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flyer-navigation">
            <button 
              className="nav-btn prev-btn"
              onClick={prevFlyer}
              disabled={isFlipping}
            >
              <HiChevronLeft />
            </button>

            <div className="flyer-indicators" tabIndex={0} aria-label="Page actuelle">
              <span className="flyers-page-number">
                {validCurrentFlyer + 1} / {flyersData.flyers.length}
              </span>
            </div>

            <button 
              className="nav-btn next-btn"
              onClick={nextFlyer}
              disabled={isFlipping}
            >
              <HiChevronRight />
            </button>
          </div>

          <div className="flyers-grid">
            {flyersData.flyers.map((flyer, index) => (
              <div 
                key={flyer.id} 
                className={`flyer-thumbnail ${index === validCurrentFlyer ? 'active' : ''}`}
                onClick={() => goToFlyer(index)}
              >
                <div className="thumbnail-book">
                  <img 
                    src={flyer.image}
                    alt={flyer.title}
                    onError={(e) => {
                      e.target.src = GSIDAPDF;
                    }}
                  />
                </div>
                <h4>{flyer.title}</h4>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NosFlyers;
    

